import React, { useContext, Fragment, useMemo } from 'react';
import { Tooltip } from '@material-ui/core';
import { PatientChartContext } from '../../../contexts/patientChart';
import {
  KIT, KIT_EMAIL, PRESCRIPTION, PRODUCT, ALLOWED_KEY_CODE,
  APPROVAL_VALIDATION_FIELD as AVF, APPROVAL_VALIDATION_ERROR,
  SF_EMAIL_TEMPLATES, JODIT_CONFIG,
} from '../../../constants/patientChart.constant';
import Input from '../../subComponents/input';
import Select from '../../subComponents/select';
import Editor, { pickEditor } from '../../subComponents/editor/jodit/v2';
import useValidationError from '../../../hooks/useValidationError';
import { IEmailTemplate } from '../../../constants/visits.constant';
import { FEATURE } from '../../../utils/feature';

const hasFeature = FEATURE();

const BottomGutter = new Map([
  [true, 'm-b-5'],
  [false, ''],
]);

const extractEmailFromKit = ({ kitKey = '', kit = KIT() }) => {
  const _kitCollection = new Map(kit);
  const _kit = _kitCollection.get(kitKey);
  const _email = _kit.email;
  return { _kitCollection, _email };
};

const extractProductFromKit = ({ kitKey = '', kit = KIT(), prescription = PRESCRIPTION() }) => {
  const _kitCollection = new Map(kit);
  const _kit = _kitCollection.get(kitKey);
  const _prescription = _kit.items.find(({ ProductId }) => ProductId === prescription.ProductId);
  return { _kitCollection, _prescription, };
};

const Label = new Map([
  [true, 'Prescribed/Kit:'],
  [false, ''],
]);

const Heading = ({
  title = '',
}) => (
  <div className='product-box-heading bg-dark-primary-v2 color-white min-height-30 p-l-5 p-r-5 p-t-1 p-b-1 flex align-items-center gap-12'>
    <div className='flex flex-fill max-width-480 min-width-480 align-items-center'>{Label.get(title.length > 0)} {title}</div>
    <div className='flex width-140 align-items-center'>Strength</div>
    <div className='flex width-100 align-items-center'>Dispense</div>
    <div className='flex width-100 align-items-center'>Days Supply</div>
    <div className='flex width-80 align-items-center'>Refills</div>
  </div>
);

const ProductRow = ({
  kitKey = '',
  kitPId = 0,
  product = PRODUCT(),
  prescription = PRESCRIPTION(),
  validationError = APPROVAL_VALIDATION_ERROR(),

  onSupplyKeyDown: handleSupplyKeyDown = () => {},
  onSupplyChange: handleSupplyChange = () => {},
  onRefillChange: handleRefillChange = () => {},
}) => {
  const daysSupplyError = useValidationError({
    errorKey: `${kitKey}-${AVF.DaysSupply}`,
    validationError,
  });

  const refillsError = useValidationError({
    errorKey: `${kitKey}-${AVF.Refills}`,
    validationError,
  });

  const title = useMemo(() => {
    if (product?.ProductName?.length) {
      return product.ProductName;
    }

    if (prescription?.ProductTitle?.length) {
      return prescription.ProductTitle;
    }

    if (prescription?.Prescribed?.length) {
      return prescription.Prescribed;
    }

    return '';
  }, [kitPId, product, prescription]);

  const strength = useMemo(() => `${prescription.Strength}${prescription.StrengthMeasure}`, [prescription]);

  return (
    <div className='flex align-items-flex-start gap-12 m-b-3'>
      <div className='flex flex-fill max-width-480 min-width-480 min-height-30 align-items-center bold underline color-primary-v4'>
        {title}
      </div>
      <Tooltip title={strength} placement='top' arrow>
        <div className='flex width-140 min-height-30 align-items-center overflow-hidden'>{strength}</div>
      </Tooltip>
      <div className='flex width-100 min-height-30 align-items-center overflow-hidden'>{prescription.TotalPills} {prescription.ProductType}</div>
      <div className='flex width-100 align-items-center'>
        <Input
          name='noOfDaysToSupply'
          type='text'
          label=''
          className='flex flex-fill'
          labelClassName=''
          maxLength={3}
          value={prescription.DaysSupply}
          error={daysSupplyError.has}
          onChange={handleSupplyChange}
          onKeyDown={handleSupplyKeyDown}
        />
      </div>
      <div className='flex width-80 align-items-center'>
        <Select
          id='refillId'
          labelId="refillId"
          disabled={!prescription?.Refills?.IsEditable}
          value={prescription?.Refills?.DefaultOption}
          error={refillsError.has}
          onChange={handleRefillChange}
          items={prescription?.Refills?.RefillOptions || []}
        />
      </div>
    </div>
  );
};

const PharmacyName = ({
  prescription = PRESCRIPTION(),
}) => (
  <div className='flex align-items-center gap-8 m-b-3'>
    <div className='flex'>Pharmacy Name:</div>
    <div className='flex'>{prescription.Pharmacy}</div>
  </div>
);

const PharmacyNote = ({
  kitKey = '',
  prescription = PRESCRIPTION(),
  validationError = APPROVAL_VALIDATION_ERROR(),

  onPharmacyNoteChange: handlePharmacyNoteChange = () => {},
}) => {
  const noteError = useValidationError({
    errorKey: `${kitKey}-${AVF.Note}`,
    validationError,
  });

  return (
    <>
      <div className='flex align-items-center justify-content-space-between gap-8 m-b-1'>
        <div className='flex align-items-center gap-8'>
          <div className='flex'>Pharmacy Note:</div>
          <div className='flex'>{prescription.NotePrefix}</div>
        </div>
  
        <div className='flex align-items-center'>
          <div className='flex'>{prescription.SubstitutionAllowed}</div>
        </div>
      </div>
  
      <div className='flex align-items-center m-b-3'>
        <Input
          name='pharmacyNote'
          type='text'
          label=''
          className='flex flex-fill'
          labelClassName=''
          maxLength={255}
          value={prescription.Note}
          error={noteError.has}
          onChange={handlePharmacyNoteChange}
        />
      </div>
    </>
  );
};

const ProductExpiry = ({
  prescription = PRESCRIPTION(),
}) => (
  <div className='flex align-items-center gap-8 m-b-3'>
    <div className='flex'>EXP:</div>
    <div className='flex tracking-wide'>{prescription.Expiry}</div>
  </div>
);

const PatientDirections = ({
  kitKey = '',
  prescription = PRESCRIPTION(),
  validationError = APPROVAL_VALIDATION_ERROR(),
  maxPatientDirectionsLength = 255,

  onPatientDirectionsChange: handlePatientDirectionsChange = () => {},
}) => {
  const directionsError = useValidationError({
    errorKey: `${kitKey}-${AVF.Sig}`,
    validationError,
  });

  const patientDirections = useMemo(() => {
    try {
      return prescription.Sig.slice(0, maxPatientDirectionsLength);
    } catch (e) {
      return '';
    }
  }, [prescription.Sig, maxPatientDirectionsLength]);

  return (
    <>
      <div className='flex align-items-center gap-8 m-b-1'>
        <div className='flex'>Patient Directions:</div>
      </div>
  
      <div className='flex flex-column'>
        <textarea
          name='patientDirections'
          className={`
            flex flex-fill border-light-grey-1 min-height-80
            p-3 font-14 font-proxima-nova tracking-wide
            color-dark-grey border-box resize-none input ${directionsError.has ? 'error' : ''}
          `}
          wrap='soft'
          maxLength={maxPatientDirectionsLength}
          value={patientDirections}
          onChange={handlePatientDirectionsChange}
        />

        <div className='flex justify-content-flex-end'>
          {patientDirections.length} / {maxPatientDirectionsLength}
        </div>
      </div>
    </>
  );
};

const EmailTemplate = ({
  email = KIT_EMAIL(),
  templates = {},

  onEmailTemplateChange: handleEmailTemplateChange = () => {},
}) => (
  <>
    <div className='flex align-items-center justify-content-space-between gap-8 m-b-1'>
      <div className='flex'>Email Template:</div>
    </div>

    <div className='flex align-items-center m-b-3'>
      <Select
        id='emailTemplateId'
        labelId="emailTemplateId"
        value={email.value}
        onChange={handleEmailTemplateChange}
        items={templates.data}
      />
    </div>
  </>
);

const SubjectLine = ({
  kitKey = '',
  email = KIT_EMAIL(),
  validationError = APPROVAL_VALIDATION_ERROR(),

  onSubjectLineChange: handleSubjectLineChange = () => {},
}) => {
  const subjectLineError = useValidationError({
    errorKey: `${kitKey}-${AVF.subjectLine}`,
    validationError,
  });

  return (
    <>
      <div className='flex align-items-center justify-content-space-between gap-8 m-b-1'>
        <div className='flex'>Subject Line:</div>
      </div>
  
      <div className='flex align-items-center m-b-3'>
        <Input
          name='subjectLine'
          type='text'
          label=''
          className='flex flex-fill'
          labelClassName=''
          maxLength={255}
          value={email.subjectLine}
          error={subjectLineError.has}
          onChange={handleSubjectLineChange}
        />
      </div>
    </>
  );
};

const EmailMessage = ({
  kitKey = '',
  email = KIT_EMAIL(),
  validationError = APPROVAL_VALIDATION_ERROR(),
  joditConfig = JODIT_CONFIG(),

  onEmailMessageChange: handleEmailMessageChange = () => {},
}) => {
  const emailMessageError = useValidationError({
    errorKey: `${kitKey}-${AVF.emailMessage}`,
    validationError,
  });

  const EmailInputCollection = useMemo(() => new Map([
    [false, () => (
      <textarea
        name='emailMessage'
        className={`
          flex flex-fill border-light-grey-1 min-height-320
          p-3 font-14 font-proxima-nova tracking-wide
          color-dark-grey border-box resize-none input ${emailMessageError.has ? 'error' : ''}
        `}
        wrap='soft'
        value={email.emailMessage}
        onChange={handleEmailMessageChange}
      />
    )],
    [true, () => (
      <Editor
        key={joditConfig.id}
        id={joditConfig.id}
        name={joditConfig.name}
        className={joditConfig.className}
        editorRef={joditConfig.editorRef}
        value={joditConfig.value}
        extras={joditConfig.extras}
        onChange={joditConfig.onChange}
      />
    )],
  ]), [
    email,
    emailMessageError,
    handleEmailMessageChange,
    joditConfig,
  ]);

  const EmailInput = useMemo(() => EmailInputCollection.get(hasFeature.RICH_TEXT_EDITOR), [EmailInputCollection]);

  return (
    <div className='flex align-items-center height-460'>
      <EmailInput />
    </div>
  );
};

const EmailTemplateWrapper = ({
  kitKey = '',
  email = KIT_EMAIL(),
  templates = SF_EMAIL_TEMPLATES(),
  validationError = APPROVAL_VALIDATION_ERROR(),
  joditConfig = JODIT_CONFIG(),
}) => {
  const {
    kit,
    setKit,
  } = useContext(PatientChartContext);

  const { _kitCollection, _email } = useMemo(() => extractEmailFromKit({
    kitKey,
    kit,
  }), [
    kitKey,
    kit,
  ]);

  const emailCollection = useMemo(() => {
    const collection = new Map([
      [-1, IEmailTemplate()],
    ]);
    collection.delete(-1);

    for (let i = 0; i < templates.data.length; i++) {
      const template = templates.data[i];
      collection.set(template.value, template);
    }
    return collection;
  }, [templates.data]);

  const handleSubjectLineChange = ({ target: { value } }) => {
    _email.subjectLine = value;
    setKit(_kitCollection);
  };

  const handleEmailTemplateChange = ({ target: { value } }) => {
    _email.value = value;
    const template = emailCollection.get(value) || { title: '', subject: '', body: '' };
    _email.templateTitle = template?.title || '';
    _email.subjectLine = template?.subject || '';
    const message = (template?.body || '').trim();
    _email.emailMessage = message.replace(/(<br>)/gmi, '\n');
    setKit(_kitCollection);

    if (hasFeature.RICH_TEXT_EDITOR) {
      try {
        const editor = pickEditor({
          name: joditConfig.name,
          editorCollection: joditConfig.editorRef.current,
        });
        editor.value = message;
      } catch(e) {
  
      }
    }
  };

  const handleEmailMessageChange = ({ target: { value } }) => {
    _email.emailMessage = value;
    setKit(_kitCollection);
  };

  return (
    <>
      <EmailTemplate
        kitKey={kitKey}
        email={email}
        templates={templates}
        validationError={validationError}
        onEmailTemplateChange={handleEmailTemplateChange}
      />
      <SubjectLine
        kitKey={kitKey}
        email={email}
        validationError={validationError}
        onSubjectLineChange={handleSubjectLineChange}
      />
      <EmailMessage
        kitKey={kitKey}
        email={email}
        joditConfig={joditConfig}
        validationError={validationError}
        onEmailMessageChange={handleEmailMessageChange}
      />
    </>
  );
};

const Devider = new Map([
  [true, (
    <div className='flex flex-fill m-t-5 m-b-5 border-top-2-bg-dark-primary-v4' />
  )],
  [false, <div className='flex flex-fill m-b-5' />],
]);

const ProductListing = ({
  kitKey = '',
  kitPId = 0,
  prescription = PRESCRIPTION(),
  productCollection = new Map([
    [0, PRODUCT()],
  ]),
  length = 0,
  validationError = APPROVAL_VALIDATION_ERROR(),
}) => {
  const {
    kit,
    setKit,
  } = useContext(PatientChartContext);

  const { _kitCollection, _prescription } = useMemo(() => extractProductFromKit({
    kitKey,
    kit,
    prescription,
  }), [
    kitKey,
    kit,
    prescription,
  ]);

  const handleSupplyKeyDown = (event) => {
    if (!ALLOWED_KEY_CODE.has(event.keyCode)) {
      event.preventDefault();
    }
  };

  const handleSupplyChange = ({ target: { value } }) => {
    _prescription.DaysSupply = value;
    setKit(_kitCollection);
  };

  const handleRefillChange = ({ target: { value } }) => {
    _prescription.Refills.DefaultOption = value;
    setKit(_kitCollection);
  };

  const handlePharmacyNoteChange = ({ target: { value } }) => {
    _prescription.Note = value;
    setKit(_kitCollection);
  };

  const handlePatientDirectionsChange = ({ target: { value } }) => {
    _prescription.Sig = value;
    setKit(_kitCollection);
  };

  return (
    <Fragment key={prescription.ProductId}>
      <div className='p-l-5 p-r-5'>
        <ProductRow
          kitKey={kitKey}
          kitPId={kitPId}
          product={productCollection.get(prescription.ProductId)}
          prescription={prescription}
          validationError={validationError}
          onRefillChange={handleRefillChange}
          onSupplyKeyDown={handleSupplyKeyDown}
          onSupplyChange={handleSupplyChange}
        />
        <PharmacyName
          kitKey={kitKey}
          prescription={prescription}
          validationError={validationError}
        />
        <ProductExpiry
          prescription={prescription}
        />
        <PharmacyNote
          kitKey={kitKey}
          prescription={prescription}
          validationError={validationError}
          onPharmacyNoteChange={handlePharmacyNoteChange}
        />
        <PatientDirections
          kitKey={kitKey}
          prescription={prescription}
          validationError={validationError}
          onPatientDirectionsChange={handlePatientDirectionsChange}
        />
      </div>

      {Devider.get(length > 1)}
    </Fragment>
  );
};

export const ProductBox = ({
  kitKey = '',
  kitPId = 0,
  prescriptions = [PRESCRIPTION()],
  productCollection = new Map([
    [0, PRODUCT()],
  ]),
  email = KIT_EMAIL(),
  templates = {},
  bottomGutter = true,
  validationError = APPROVAL_VALIDATION_ERROR(),
  joditConfig = JODIT_CONFIG(),
}) => {
  const kitTitle = useMemo(() => {
    if (!productCollection.has(kitPId)) {
      return '';
    }

    const product = productCollection.get(kitPId);
    return product.displayTitle;
  }, [kitPId, productCollection]);

  const productIds = useMemo(() => {
    /**
     * @type {Set<number>}
     */
    const ids = new Set([kitPId]);

    prescriptions.forEach(({ ProductId }) => {
      if (ProductId) {
        ids.add(ProductId);
      }
    });

    return ids;
  }, [kitPId, prescriptions]);

  /**
   * @type {{
   *  pId: number;
   *  title: string;
   *  subject: string;
   * }[];}
   */
  const filteredEmailTemplates = useMemo(() => {
    if (!templates?.data?.length) {
      return [];
    }
    return templates.data.filter(({ pId }) => productIds.has(pId));
  }, [productIds, templates]);

  const modifiedTemplates = useMemo(() => ({
    ...templates,
    data: filteredEmailTemplates,
  }), [templates, filteredEmailTemplates]);

  return (
    <div className={`product-box flex flex-column border-2-bg-dark-primary-v2 ${BottomGutter.get(bottomGutter)}`}>
      
      <Heading title={kitTitle} />

      <div className='product-box-content bg-white p-t-5 p-b-5'>
        {
          prescriptions.map((prescription) => (
            <ProductListing
              key={prescription.ProductId}
              kitKey={kitKey}
              kitPId={kitPId}
              prescription={prescription}
              productCollection={productCollection}
              length={prescriptions.length}
              validationError={validationError}
            />
          ))
        }

        <div className='p-l-5 p-r-5'>
          <EmailTemplateWrapper
            kitKey={kitKey}
            email={email}
            templates={modifiedTemplates}
            validationError={validationError}
            joditConfig={joditConfig}
          />
        </div>
      </div>

    </div>
  );
};

export default ProductBox;
